import { WbAutocomplete, WbTextField } from '@agilelab/plugin-wb-platform';
import { FormControl, FormHelperText } from '@material-ui/core';
import lodash from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';
import { fromZodError } from 'zod-validation-error';
import { extractCustomProperties, isHidden } from '../../utils';
import {
  Descriptor,
  DescriptorPickerProps,
  DescriptorPickerSchema,
  DescriptorPickerSchemaZod,
  Option,
  SourceType,
} from './schema';
import { getSourceType } from './sourceTypes';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

export function isObjectInPath(
  descriptor: { [key: string]: any },
  path: string,
): boolean {
  return lodash.isPlainObject(lodash.get(descriptor, path, undefined));
}

export function isArrayInPath(
  descriptor: { [key: string]: any },
  path: string,
): boolean {
  return Array.isArray(lodash.get(descriptor, path, undefined));
}

function parseOptionsFromArray(
  descriptor: { [key: string]: any } | undefined,
  optionsAt: string,
  optionsDisplayNameAt?: string,
): Option[] {
  return lodash
    .get(descriptor, optionsAt, [])
    .map((item: { [x: string]: any }, index: number) => {
      const optionDisplayName =
        optionsDisplayNameAt && item[optionsDisplayNameAt]
          ? item[optionsDisplayNameAt]
          : item;

      return {
        label:
          typeof optionDisplayName === 'string'
            ? optionDisplayName
            : String(index),
        key: String(index),
        value: item,
      };
    });
}

function parseOptionsFromObject(
  descriptor: { [key: string]: any } | undefined,
  optionsAt: string,
  optionsDisplayNameAt?: string,
): Option[] {
  const objectParsed: { [key: string]: any } = lodash.get(
    descriptor,
    optionsAt,
    {},
  );

  return Object.keys(objectParsed).map(key => ({
    label: optionsDisplayNameAt
      ? lodash.get(objectParsed[key], optionsDisplayNameAt, key)
      : key,
    key: key,
    value: objectParsed[key],
  }));
}

function getOptionKey(
  formContext: { [key: string]: any },
  fieldName: string,
): string | undefined {
  return (formContext[fieldName] as Option)?.key;
}

export function parseOptions(
  descriptor: { [key: string]: any } | undefined,
  optionsAt: string,
  optionsDisplayNameAt?: string,
): Option[] {
  if (!descriptor || Object.keys(descriptor).length === 0) {
    return [];
  }

  const parseArray = isArrayInPath(descriptor, optionsAt);
  const parseObject = isObjectInPath(descriptor, optionsAt);

  if (parseArray) {
    return parseOptionsFromArray(descriptor, optionsAt, optionsDisplayNameAt);
  }

  if (parseObject) {
    return parseOptionsFromObject(descriptor, optionsAt, optionsDisplayNameAt);
  }

  if (!parseArray && !parseObject) {
    throw new Error(
      `The path specified at 'optionsAt' is neither an object nor a list. Please contact the platform team. (optionsAt = "${optionsAt}")`,
    );
  }

  return [];
}

export function getSourceDescriptor(
  formContext: { [key: string]: any },
  sourceType: SourceType,
  source: string,
): Descriptor | undefined {
  const parsedDescriptor = lodash.get(
    formContext,
    sourceType.resolveSource(source),
    undefined,
  );

  return sourceType.mapDescriptor(parsedDescriptor);
}

function parseOption(input: any): Option | null {
  const optionKeys = ['key', 'label', 'value'];
  if (
    input &&
    typeof input === 'object' &&
    optionKeys.every(key => key in input)
  ) {
    return input as Option;
  }
  return null;
}

export const DescriptorPicker = ({
  onChange,
  required,
  schema,
  uiSchema,
  rawErrors,
  idSchema,
  formData,
  formContext,
}: DescriptorPickerProps) => {
  const { title, description } = schema;

  const [pickerConfig, setPickerConfig] = useState<
    DescriptorPickerSchema | undefined
  >(undefined);
  const [pickerError, setPickerError] = useState<Error | undefined>(undefined);
  const customProps = extractCustomProperties(uiSchema);

  // Stabilize onChange ref to avoid re-triggering effects on every render
  // (RJSF recreates onChange on each render; other pickers like EntityPicker do the same)
  const onChangeRef = useLatest(onChange);

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);

  useEffect(() => {
    const parsedPickerConfig = DescriptorPickerSchemaZod.safeParse(schema);
    if (!parsedPickerConfig.success) {
      setPickerConfig(undefined);
      const errorMessage = `This field has been disabled due to a misconfiguration error. ${
        fromZodError(parsedPickerConfig.error).message
      }. Please contact the platform team
          to address this issue.`;
      setPickerError(new Error(errorMessage));
      return;
    }
    setPickerConfig(parsedPickerConfig.data);
  }, [schema]);

  const [descriptor, setDescriptor] = useState<Descriptor | undefined>();
  const [options, setOptions] = useState<Option[]>([]);
  const [value, setValue] = useState<Option | null | undefined>(
    parseOption(formData),
  );

  /**
   * A watch value to track changes from. If this value changes then the selected option in the descriptor picker is reset
   */
  const [watchOptionKey, setWatchOptionKey] = useState<string | undefined>(
    pickerConfig && pickerConfig.sourceType === 'field'
      ? getOptionKey(formContext, pickerConfig.source)
      : undefined,
  );

  // Track the source field's key to detect actual changes (not just formContext reference changes)
  const sourceFieldKey =
    pickerConfig?.sourceType === 'field' && pickerConfig?.source
      ? formContext[pickerConfig.source]?.key
      : undefined;
  const sourceFieldExists =
    pickerConfig?.sourceType === 'field' && pickerConfig?.source
      ? !!formContext[pickerConfig.source]
      : undefined;

  /**
   * Clears this field's value, preferring the dependency manager to avoid
   * going through RJSF's field onChange which can use stale internal state
   * (the Form's componentDidUpdate setState may not have been processed yet).
   */
  const clearValue = useCallback(() => {
    if (dependencyManager?.notifyUpdate && fieldPath) {
      dependencyManager.notifyUpdate({
        fieldPath,
        value: undefined,
        source: 'user',
        intent: 'manual',
      });
    } else {
      onChangeRef.current(undefined);
    }
  }, [dependencyManager, fieldPath, onChangeRef]);

  /**
   * Checks whether the dependent field changes its value, if so, the selected value for this field is set to none.
   * When watchOptionKey is undefined (component just mounted/remounted), we only sync watchOptionKey
   * without clearing the value — this prevents wiping out a valid formData value after remount.
   * If the field does not depend on any field, this effect has no effect (seems a joke, right?)
   */
  useEffect(() => {
    if (pickerConfig?.sourceType === 'field') {
      if (!sourceFieldExists) {
        setOptions([]);
        setWatchOptionKey(undefined);
        setValue(null);
        clearValue();
        return;
      }

      if (watchOptionKey !== sourceFieldKey) {
        if (watchOptionKey === undefined) {
          // Initial sync after mount/remount: just align watchOptionKey without clearing the value
          setWatchOptionKey(sourceFieldKey);
        } else {
          // Source field actually changed: clear dependent value
          setOptions([]);
          setWatchOptionKey(sourceFieldKey);
          setValue(null);
          clearValue();
        }
      }
    }
  }, [
    sourceFieldKey,
    sourceFieldExists,
    clearValue,
    pickerConfig?.sourceType,
    watchOptionKey,
  ]);

  useEffect(() => {
    if (pickerConfig?.sourceType) {
      const sourceType = getSourceType(pickerConfig.sourceType);
      if (!sourceType) {
        setPickerError(
          new Error(
            `The sourceType specified is invalid. Please contact the platform team. (sourceType = "${pickerConfig.sourceType}")`,
          ),
        );
        return;
      }

      const parsedDescriptor = getSourceDescriptor(
        formContext,
        sourceType,
        pickerConfig.source,
      );
      if (!parsedDescriptor && pickerConfig.sourceType !== 'field') {
        setPickerError(
          new Error(
            `Could not find any source descriptor in the context. Please contact the platform team. (Having sourceType = "${pickerConfig.sourceType}" and source = "${pickerConfig.source}")`,
          ),
        );
        return;
      }

      setDescriptor(parsedDescriptor);

      try {
        const parsedOptions = parseOptions(
          parsedDescriptor,
          pickerConfig.optionsAt,
          pickerConfig.optionsDisplayNameAt,
        );
        setOptions(parsedOptions);
        setPickerError(undefined);
      } catch (error) {
        setPickerError(error);
        return;
      }
    }
  }, [formContext, pickerConfig]);

  const onSelect = useCallback(
    (_: any, option: Option | null) => {
      if (!option || !descriptor) {
        // Notify dependency manager of undefined value, or fallback to onChange
        if (dependencyManager?.notifyUpdate && fieldPath) {
          dependencyManager.notifyUpdate({
            fieldPath,
            value: undefined,
            source: 'user',
            intent: 'manual',
          });
        } else {
          onChangeRef.current(undefined);
        }
        setValue(null);
        return;
      }

      const onChangeVal = {
        ...option,
        value: option.value,
      };

      setValue(option);

      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value: onChangeVal,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChangeRef.current(onChangeVal);
      }
    },
    [descriptor, onChangeRef, dependencyManager, fieldPath],
  );

  if (pickerError) {
    return (
      <FormControl
        style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
        margin="normal"
        required={required}
        error
      >
        <WbTextField
          label={title}
          margin="dense"
          helperText={description}
          FormHelperTextProps={{
            margin: 'dense',
            style: { marginLeft: 0 },
          }}
          variant="outlined"
          required={required}
          disabled
          {...customProps}
        />
        <FormHelperText id={`${idSchema?.$id}-error-text`}>
          {pickerError.message}
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl
      required={required}
      error={rawErrors?.length > 0 && !value}
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
    >
      <WbAutocomplete
        disabled={!descriptor}
        id={idSchema?.$id}
        value={value || null}
        onChange={onSelect}
        options={options || []}
        getOptionLabel={(option: Option) => option.label}
        getOptionSelected={(option: Option, selectedValue: Option) =>
          option.key === selectedValue.key
        }
        clearOnBlur
        autoSelect
        freeSolo={false}
        helperText={description}
        label={title}
        required={required}
        {...customProps}
      />
    </FormControl>
  );
};
